/* eslint-disable no-unused-vars */
// src/components/MetricsChart.tsx

import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppAuth } from 'src/context/use-app-auth';
import { chartLabels } from 'src/text/locales/default-en';
import { useTranslation } from 'src/text';
import { MetricsData, useMultiDailyMetrics } from 'src/api/hooks/use-metrics';
import { SyntraxisDateRangePicker } from 'src/components/date-picker';
import { SyntraxisChart } from 'src/components/charts';

const metricOptions: Array<{
  value: keyof MetricsData;
  label: string;
  text: string;
}> = [
  {
    value: 'callClicks',
    label: chartLabels.callClicks,
    text: chartLabels.callsText,
  },
  {
    value: 'mobileClicks',
    label: chartLabels.mobileImpressions,
    text: chartLabels.mobileImpressionsText,
  },
  {
    value: 'websiteClicks',
    label: chartLabels.websiteClicks,
    text: chartLabels.websiteText,
  },
  {
    value: 'directionClicks',
    label: chartLabels.businessDirections,
    text: chartLabels.businessDirectionsText,
  },
];

const MetricsChart: React.FC = () => {
  const { location, user } = useAppAuth();
  const { keywordLabels } = useTranslation();

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());

  const locationId = location?.key ?? '';
  const accessToken = user?.token ?? '';

  const { loading, data } = useMultiDailyMetrics({
    locationId,
    accessToken,
    startDate,
    endDate,
  });

  const handleDateChange = (
    startDate: Date | undefined,
    endDate: Date | undefined,
  ) => {
    if (startDate) {
      setStartDate(startDate);
    }
    if (endDate) {
      setEndDate(endDate);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }
  return (
    <Box sx={{ width: '100%' }} mt={8}>
      <Box
        mt={4}
        mb={4}
        sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
        <Typography variant="h3">{keywordLabels.performanceStats}</Typography>
        {keywordLabels.performanceStatsInfo && (
          <Tooltip
            title={keywordLabels.performanceStatsInfo}
            placement="right"
            arrow>
            <IconButton size="small" aria-label="About performance statistics">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <SyntraxisDateRangePicker
        onDateRangeChange={handleDateChange}
        startDate={startDate}
        endDate={endDate}
      />

      <Box mt={4}>
        <SyntraxisChart metricOptions={metricOptions} data={data} />
      </Box>
    </Box>
  );
};

export { MetricsChart };
